import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress, TextField, Typography,
} from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTranslation } from 'react-i18next';
import { REPAIR_HINT_MAX } from '../../../utilityFunctions/dashboardApi';
import HitlReviewPanel from '../../llm/HitlReviewPanel';
import { stageLabel } from './stageLabels';
import {
  AvailableData, SuggestedCharts, TechnicalDetails, diagnosisTitle, plainExplanation,
} from './GuidedFailureCard';

const GOAL_MAX = 1000;
const ANSWER_MAX = 1000;

export const emptyDraft = () => ({
  suggestionId: null, suggestionText: '', answers: {}, freeText: '', goalOpen: false, goal: null,
  remember: true, allowQuestions: true,
});

/** Dialog state → POST …/repair body (every field optional; free text is authoritative server-side). */
export const draftToRequest = (draft, originalGoal) => {
  const goal = draft.goalOpen && draft.goal ? draft.goal.trim() : '';
  return {
    hint: draft.freeText.trim() || undefined,
    suggestion_id: draft.suggestionId || undefined,
    suggestion_hint: draft.suggestionId ? (draft.suggestionText.trim() || undefined) : undefined,
    answers: Object.keys(draft.answers).filter((k) => (draft.answers[k] || '').trim())
      .map((k) => ({ question_id: k, text: draft.answers[k].trim() })),
    goal_override: goal && goal !== (originalGoal || '').trim() ? goal : undefined,
    remember: draft.remember,
    allow_questions: draft.allowQuestions,
  };
};

const hasKnowledge = (req) => !!(req.hint || req.suggestion_id || req.goal_override || (req.answers && req.answers.length));

/**
 * Post-generation HITL for one widget. Guided and expert input compose into ONE repair request:
 * pick a suggestion (and edit its text), answer the diagnosis questions, add free-form knowledge,
 * optionally redefine the widget's goal; choose whether the knowledge is remembered for the dashboard
 * and whether the run may stop to ask. `mode='expert'` opens on the free-text field with the
 * diagnosis collapsed for reference. The parent owns the run; while it waits at a clarification
 * checkpoint the shared HitlReviewPanel is shown here.
 *
 * @param draft      initial dialog state (kept by the parent so a FAILED attempt reopens pre-filled)
 * @param onSubmit(request, draft)
 */
export default function WidgetRepairDialog({
  open, mode = 'guided', widget, diagnosis, diagnosisLoading, draft: initialDraft, failure, running, runStage,
  checkpoint, jwt, busy, error, staleVersionId, knowledgeSaved, onSubmit, onClose, onReload, onCheckpointResolved,
}) {
  const { t } = useTranslation();
  const originalGoal = (widget && widget.build_plan && widget.build_plan.goal) || (widget && widget.title) || '';
  const [draft, setDraft] = React.useState(() => ({ ...emptyDraft(), ...(initialDraft || {}) }));
  React.useEffect(() => {
    if (open) setDraft({ ...emptyDraft(), ...(initialDraft || {}) });
  }, [open, initialDraft, widget && widget.widget_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!widget) return null;

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const bf = widget.build_failure || null;
  const buildFailed = !!bf || !widget.binding_hash;
  const expert = mode === 'expert';
  const disabled = running || busy || staleVersionId != null || !!checkpoint;
  const request = draftToRequest(draft, originalGoal);
  const suggestions = (diagnosis && diagnosis.suggestions) || [];
  const questions = (diagnosis && diagnosis.questions) || [];
  const tooLong = draft.freeText.length > REPAIR_HINT_MAX || draft.suggestionText.length > REPAIR_HINT_MAX;

  const pickSuggestion = (s) => (draft.suggestionId === s.id
    ? set({ suggestionId: null, suggestionText: '' })
    : set({ suggestionId: s.id, suggestionText: s.hint || '' }));

  const diagnosisBody = diagnosis ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }} data-testid="dialog-diagnosis">
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{diagnosisTitle(t, diagnosis)}</Typography>
      <Typography variant="body2">{plainExplanation(diagnosis)}</Typography>
      <AvailableData data={diagnosis.available_data} />
    </Box>
  ) : diagnosisLoading ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={14} />
      <Typography variant="caption" color="text.secondary">{t('Dashboards.diagnosis.loading')}</Typography>
    </Box>
  ) : null;

  const title = expert ? t('Dashboards.repair.expertTitle', { title: widget.title || widget.widget_id })
    : buildFailed ? t('Dashboards.repair.titleFailed', { title: widget.title || widget.widget_id })
      : t('Dashboards.repair.titleRefine', { title: widget.title || widget.widget_id });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper"
      PaperProps={{ sx: { minWidth: 0, maxWidth: { xs: 'calc(100% - 16px)', sm: 640 }, m: { xs: 1, sm: 4 } } }}>
      <DialogTitle sx={{ overflowWrap: 'anywhere', pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0, overflowWrap: 'anywhere' }}>
        {failure && (
          <Alert severity="error" sx={{ minWidth: 0, '& .MuiAlert-message': { minWidth: 0 } }} data-testid="repair-failed">
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
              {t('Dashboards.repair.stillFailingPlain')}
            </Typography>
            <Typography variant="body2">
              {(failure.diagnosis && plainExplanation(failure.diagnosis)) || t('Dashboards.diagnosis.genericExplanation')}
            </Typography>
            <TechnicalDetails stage={failure.stage} message={failure.note} lastSql={failure.lastSql}
              code={failure.diagnosis && failure.diagnosis.failure_code} />
          </Alert>
        )}
        {checkpoint && (
          <Box sx={{ minWidth: 0 }} data-testid="repair-checkpoint">
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{t('Dashboards.repair.needsAnswer')}</Typography>
            <HitlReviewPanel key={checkpoint.checkpointId} checkpoint={checkpoint} jwtToken={jwt} onResolved={onCheckpointResolved} />
          </Box>
        )}
        {staleVersionId != null && (
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={onReload}>{t('Dashboards.repair.reload')}</Button>}>
            {t('Dashboards.repair.stale', { id: staleVersionId })}
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        {!expert && diagnosisBody}
        {expert && diagnosis && (
          <Accordion disableGutters variant="outlined" sx={{ minWidth: 0 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('Dashboards.repair.diagnosisForReference')}</Typography>
            </AccordionSummary>
            <AccordionDetails>{diagnosisBody}</AccordionDetails>
          </Accordion>
        )}

        {suggestions.length > 0 && (
          <Box sx={{ minWidth: 0 }}>
            <SuggestedCharts suggestions={suggestions} disabled={disabled} selectedId={draft.suggestionId}
              onPick={pickSuggestion} />
            {draft.suggestionId && (
              <TextField value={draft.suggestionText} onChange={(e) => set({ suggestionText: e.target.value })}
                multiline minRows={2} maxRows={6} fullWidth size="small" sx={{ mt: 1 }} disabled={disabled}
                label={t('Dashboards.repair.suggestionLabel')}
                inputProps={{ 'aria-label': t('Dashboards.repair.suggestionLabel'), maxLength: REPAIR_HINT_MAX }} />
            )}
          </Box>
        )}

        {questions.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{t('Dashboards.diagnosis.questionsTitle')}</Typography>
            {questions.map((q) => (
              <TextField key={q.id} value={draft.answers[q.id] || ''} size="small" fullWidth multiline maxRows={4}
                disabled={disabled} label={q.text} placeholder={q.placeholder || t('Dashboards.repair.answerPlaceholder')}
                onChange={(e) => set({ answers: { ...draft.answers, [q.id]: e.target.value } })}
                inputProps={{ 'aria-label': q.text, maxLength: ANSWER_MAX }} />
            ))}
          </Box>
        )}

        <TextField
          value={draft.freeText}
          onChange={(e) => set({ freeText: e.target.value })}
          multiline minRows={expert ? 4 : 2} maxRows={8} fullWidth disabled={disabled} autoFocus={expert}
          label={t('Dashboards.repair.yourKnowledge')}
          placeholder={t('Dashboards.repair.hintPlaceholder')}
          error={tooLong}
          helperText={`${draft.freeText.length}/${REPAIR_HINT_MAX} · ${t('Dashboards.repair.freeTextWins')}`}
          inputProps={{ 'aria-label': t('Dashboards.repair.yourKnowledge'), maxLength: REPAIR_HINT_MAX }}
        />

        <Box sx={{ minWidth: 0 }}>
          {!draft.goalOpen ? (
            <Button size="small" onClick={() => set({ goalOpen: true, goal: draft.goal != null ? draft.goal : originalGoal })}
              disabled={disabled} sx={{ textTransform: 'none', px: 0.5 }}>
              {t('Dashboards.repair.goalToggle')}
            </Button>
          ) : (
            <TextField value={draft.goal || ''} onChange={(e) => set({ goal: e.target.value })} size="small" fullWidth multiline
              maxRows={4} disabled={disabled} label={t('Dashboards.repair.goalLabel')} helperText={t('Dashboards.repair.goalHelp')}
              inputProps={{ 'aria-label': t('Dashboards.repair.goalLabel'), maxLength: GOAL_MAX }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={draft.remember} disabled={disabled} onChange={(e) => set({ remember: e.target.checked })} />}
            label={<Typography variant="body2">{t('Dashboards.repair.remember')}</Typography>} />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.75 }}>
            {draft.remember ? t('Dashboards.repair.knowledgeNote') : t('Dashboards.repair.rememberOff')}
          </Typography>
          <FormControlLabel
            control={<Checkbox size="small" checked={draft.allowQuestions} disabled={disabled}
              onChange={(e) => set({ allowQuestions: e.target.checked })} />}
            label={<Typography variant="body2">{t('Dashboards.repair.allowQuestions')}</Typography>} />
        </Box>

        {knowledgeSaved && (
          <Typography variant="caption" color="success.main" data-testid="knowledge-saved">{t('Dashboards.repair.knowledgeSaved')}</Typography>
        )}
        {running && (
          <Box data-testid="repair-running">
            <Typography variant="caption" color="text.secondary">
              {t('Dashboards.repair.running', { stage: stageLabel(t, runStage) || t('Dashboards.run.stages.repair') })}
            </Typography>
            <LinearProgress sx={{ borderRadius: 1, mt: 0.5 }} />
          </Box>
        )}
        {bf && <TechnicalDetails stage={bf.stage} message={bf.message || widget.status_note} lastSql={bf.last_sql}
          code={diagnosis && diagnosis.failure_code} />}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('Dashboards.common.close')}</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<ReplayIcon />} disabled={disabled}
          onClick={() => onSubmit({ remember: draft.remember, allow_questions: draft.allowQuestions }, draft)}
          sx={{ textTransform: 'none' }}>
          {buildFailed ? t('Dashboards.repair.retry') : t('Dashboards.repair.regenerate')}
        </Button>
        <Button variant="contained" startIcon={<BuildOutlinedIcon />} disabled={disabled || tooLong || !hasKnowledge(request)}
          onClick={() => onSubmit(request, draft)} sx={{ textTransform: 'none' }}>
          {t('Dashboards.repair.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
